import React, { useState } from 'react';
import ReactEcharts from 'echarts-for-react';

const EnhancedChart = ({ months, data, dataType, countMode = 'unique' }) => {
    const [chartType, setChartType] = useState('line');
    const [showArea, setShowArea] = useState(false);
    const [selectedCohorts, setSelectedCohorts] = useState([]);
    
    const getChartOptions = () => {
        const xAxisLegend = months;
        const xAxisDataAll = Object.keys(data).map(el => data[el].result.filter(val => val !== null).reduce((acc, val) => acc + val, 0));
        const xAxisDataNew = Object.keys(data).map(el => data[el].total);
        const xAxisDataOld = Object.keys(data).map(el => data[el].result.filter(val => val !== null).reduce((acc, val) => acc + val, 0) - data[el].total);
        
        // Calculate retention rates
        const retentionRates = Object.keys(data).map(month => {
            const monthData = data[month];
            const rates = monthData.result.map((val, idx) => {
                const baseValue = data[months[idx]]?.total;
                return baseValue ? (val / baseValue * 100).toFixed(2) : 0;
            });
            return rates;
        });

        const baseOptions = {
            title: {
                text: 'Analyse de Cohorte',
                subtext: countMode === 'all' ? 'Consultations' : 'Clients',
                left: 'center'
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                },
                formatter: function(params) {
                    let result = params[0].name + '<br/>';
                    params.forEach(item => {
                        result += `${item.marker} ${item.seriesName}: ${item.value}<br/>`;
                    });
                    return result;
                }
            },
            toolbox: {
                feature: {
                    dataZoom: {
                        yAxisIndex: 'none'
                    },
                    restore: {},
                    saveAsImage: {}
                }
            },
            legend: {
                data: countMode === 'all' ?
                    ['Total de consultations', 'Consultations nouveaux clients', 'Consultations clients récurrents'] :
                    ['Total clients', 'Nouveaux clients', 'Clients récurrents'],
                bottom: 0
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '15%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: chartType === 'bar',
                data: xAxisLegend,
                axisLabel: {
                    rotate: 45,
                    interval: 0
                }
            },
            yAxis: {
                type: 'value',
                name: countMode === 'all' ? 'Nombre de consultations' : 'Nombre de clients'
            },
            series: [
                {
                    name: countMode === 'all' ? 'Total de consultations' : 'Total clients',
                    type: chartType,
                    data: xAxisDataAll,
                    smooth: true,
                    areaStyle: showArea ? {} : null,
                    emphasis: {
                        focus: 'series'
                    },
                    itemStyle: {
                        color: '#2196F3'
                    }
                },
                {
                    name: countMode === 'all' ? 'Consultations nouveaux clients' : 'Nouveaux clients',
                    type: chartType,
                    data: xAxisDataNew,
                    smooth: true,
                    areaStyle: showArea ? {} : null,
                    emphasis: {
                        focus: 'series'
                    },
                    itemStyle: {
                        color: '#4CAF50'
                    }
                },
                {
                    name: countMode === 'all' ? 'Consultations clients récurrents' : 'Clients récurrents',
                    type: chartType,
                    data: xAxisDataOld,
                    smooth: true,
                    areaStyle: showArea ? {} : null,
                    emphasis: {
                        focus: 'series'
                    },
                    itemStyle: {
                        color: '#FF9800'
                    }
                }
            ]
        };

        return baseOptions;
    };

    const getHeatmapOptions = () => {
        const months = Object.keys(data);
        const heatmapData = [];

        months.forEach((month, yIndex) => {
            data[month].result.forEach((value, xIndex) => {
                const baseValue = data[months[xIndex]]?.total;
                const retention = baseValue ? (value / baseValue * 100) : 0;
                heatmapData.push([xIndex, yIndex, retention.toFixed(2)]);
            });
        });

        return {
            title: {
                text: 'Heatmap de Rétention',
                left: 'center'
            },
            tooltip: {
                position: 'top',
                formatter: function(params) {
                    return `${months[params.data[1]]} → ${months[params.data[0]]}<br/>Rétention: ${params.data[2]}%`;
                }
            },
            grid: {
                height: '70%',
                top: '10%'
            },
            xAxis: {
                type: 'category',
                data: months,
                splitArea: {
                    show: true
                },
                axisLabel: {
                    rotate: 45,
                    interval: 0
                }
            },
            yAxis: {
                type: 'category',
                data: months,
                splitArea: {
                    show: true
                }
            },
            visualMap: {
                min: 0,
                max: 100,
                calculable: true,
                orient: 'horizontal',
                left: 'center',
                bottom: '5%',
                inRange: {
                    color: ['#f5f5f5', '#ffebee', '#ffcdd2', '#ef5350', '#d32f2f', '#b71c1c']
                }
            },
            series: [{
                name: 'Retention Rate',
                type: 'heatmap',
                data: heatmapData,
                label: {
                    show: true,
                    formatter: function(params) {
                        return params.data[2] + '%';
                    }
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 10,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                }
            }]
        };
    };

    const getRetentionCurvesOptions = () => {
        const colors = [
            '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
            '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
            '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5'
        ];

        const cohortsToShow = selectedCohorts.length > 0 ? selectedCohorts : Object.keys(data).slice(-12);

        const series = cohortsToShow.map((cohort, index) => {
            const cohortData = data[cohort];
            if (!cohortData) return null;

            const retentionRates = cohortData.result.map((val, idx) => {
                if (val === null || idx < months.indexOf(cohort)) return null;
                const percentage = cohortData.total > 0 ? (val / cohortData.total * 100) : 0;
                return percentage.toFixed(2);
            }).filter((val, idx) => idx >= months.indexOf(cohort));

            return {
                name: `Cohorte ${cohort}`,
                type: 'line',
                smooth: true,
                data: retentionRates,
                itemStyle: {
                    color: colors[index % colors.length]
                },
                emphasis: {
                    focus: 'series'
                }
            };
        }).filter(s => s !== null);

        const maxLength = series.length > 0 ? Math.max(...series.map(s => s.data.length)) : 0;

        return {
            title: {
                text: 'Courbes de Rétention par Cohorte',
                subtext: 'Pourcentage de rétention au fil du temps',
                left: 'center'
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                },
                formatter: function(params) {
                    let result = 'Période ' + params[0].axisValue + '<br/>';
                    params.forEach(item => {
                        if (item.value !== null) {
                            result += `${item.marker} ${item.seriesName}: ${item.value}%<br/>`;
                        }
                    });
                    return result;
                }
            },
            toolbox: {
                feature: {
                    dataZoom: {
                        yAxisIndex: 'none'
                    },
                    restore: {},
                    saveAsImage: {}
                }
            },
            legend: {
                type: 'scroll',
                bottom: 0,
                data: series.map(s => s.name)
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '20%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: Array.from({ length: maxLength }, (_, i) => `Mois ${i}`),
                name: 'Mois depuis inscription'
            },
            yAxis: {
                type: 'value',
                name: 'Taux de rétention (%)',
                min: 0,
                max: 100
            },
            series: series
        };
    };

    const getCumulativeRetentionOptions = () => {
        const colors = [
            '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
            '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
        ];

        const cohortsToShow = selectedCohorts.length > 0 ? selectedCohorts : Object.keys(data).slice(-12);

        const series = cohortsToShow.map((cohort, index) => {
            const cohortData = data[cohort];
            if (!cohortData) return null;

            const cumulativeRates = [];
            let cumulative = 0;

            cohortData.result.forEach((val, idx) => {
                if (idx < months.indexOf(cohort)) return;
                if (val !== null) {
                    const percentage = cohortData.total > 0 ? (val / cohortData.total * 100) : 0;
                    cumulative += percentage;
                    cumulativeRates.push(cumulative.toFixed(2));
                }
            });

            return {
                name: `Cohorte ${cohort}`,
                type: 'line',
                smooth: true,
                data: cumulativeRates,
                itemStyle: {
                    color: colors[index % colors.length]
                },
                areaStyle: showArea ? { opacity: 0.3 } : null,
                emphasis: {
                    focus: 'series'
                }
            };
        }).filter(s => s !== null);

        const maxLength = series.length > 0 ? Math.max(...series.map(s => s.data.length)) : 0;

        return {
            title: {
                text: 'Rétention Cumulée par Cohorte',
                subtext: 'Pourcentage de rétention cumulé',
                left: 'center'
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                }
            },
            toolbox: {
                feature: {
                    dataZoom: {
                        yAxisIndex: 'none'
                    },
                    restore: {},
                    saveAsImage: {}
                }
            },
            legend: {
                type: 'scroll',
                bottom: 0,
                data: series.map(s => s.name)
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '20%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: Array.from({ length: maxLength }, (_, i) => `Mois ${i}`),
                name: 'Mois depuis inscription'
            },
            yAxis: {
                type: 'value',
                name: 'Rétention cumulée (%)'
            },
            series: series
        };
    };

    const getAverageRetentionCurveOptions = () => {
        const periodLengths = Object.keys(data).map(cohort => {
            return data[cohort].result.filter((v, idx) => idx >= months.indexOf(cohort) && v !== null).length;
        });
        const maxPeriods = periodLengths.length > 0 ? Math.max(...periodLengths) : 0;

        const averageData = [];

        for (let period = 0; period < maxPeriods; period++) {
            let sum = 0;
            let count = 0;

            Object.keys(data).forEach(cohort => {
                const cohortIndex = months.indexOf(cohort);
                const dataIndex = cohortIndex + period;

                if (dataIndex < data[cohort].result.length && data[cohort].result[dataIndex] !== null) {
                    const retention = (data[cohort].result[dataIndex] / data[cohort].total * 100);
                    sum += retention;
                    count++;
                }
            });

            averageData.push(count > 0 ? (sum / count).toFixed(2) : null);
        }

        return {
            title: {
                text: 'Courbe de Rétention Moyenne',
                subtext: 'Taux de rétention moyen sur toutes les cohortes',
                left: 'center'
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                }
            },
            toolbox: {
                feature: {
                    dataZoom: {
                        yAxisIndex: 'none'
                    },
                    restore: {},
                    saveAsImage: {}
                }
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '10%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: Array.from({ length: maxPeriods }, (_, i) => `Mois ${i}`),
                name: 'Mois depuis inscription'
            },
            yAxis: {
                type: 'value',
                name: 'Taux de rétention moyen (%)',
                min: 0,
                max: 100
            },
            series: [
                {
                    name: 'Rétention moyenne',
                    type: 'line',
                    smooth: true,
                    data: averageData,
                    itemStyle: {
                        color: '#1976d2'
                    },
                    areaStyle: showArea ? {
                        color: {
                            type: 'linear',
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [
                                { offset: 0, color: 'rgba(25, 118, 210, 0.3)' },
                                { offset: 1, color: 'rgba(25, 118, 210, 0.05)' }
                            ]
                        }
                    } : null,
                    markLine: {
                        silent: true,
                        lineStyle: {
                            color: '#ff5722',
                            type: 'dashed'
                        },
                        data: [
                            { yAxis: 50, label: { formatter: 'Objectif 50%' } }
                        ]
                    }
                }
            ]
        };
    };

    const getHorizontalCohortCountOptions = () => {
        const colors = [
            '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
            '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf',
            '#aec7e8', '#ffbb78', '#98df8a', '#ff9896', '#c5b0d5'
        ];

        const cohortsToShow = selectedCohorts.length > 0 ? selectedCohorts : Object.keys(data).slice(-12);

        const series = cohortsToShow.map((cohort, index) => {
            const cohortData = data[cohort];
            if (!cohortData) return null;

            const counts = cohortData.result.map((val, idx) => {
                if (val === null || idx < months.indexOf(cohort)) return null;
                return val;
            }).filter((val, idx) => idx >= months.indexOf(cohort));

            return {
                name: `Cohorte ${cohort}`,
                type: 'line',
                smooth: true,
                data: counts,
                itemStyle: {
                    color: colors[index % colors.length]
                },
                emphasis: {
                    focus: 'series'
                }
            };
        }).filter(s => s !== null);

        const maxLength = series.length > 0 ? Math.max(...series.map(s => s.data.length)) : 0;

        return {
            title: {
                text: 'Nombre de Clients par Période (Vue Horizontale)',
                subtext: 'Évolution du nombre de clients au fil du temps',
                left: 'center'
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross'
                },
                formatter: function(params) {
                    let result = 'Période ' + params[0].axisValue + '<br/>';
                    params.forEach(item => {
                        if (item.value !== null && item.value !== undefined) {
                            result += `${item.marker} ${item.seriesName}: ${item.value} clients<br/>`;
                        }
                    });
                    return result;
                }
            },
            toolbox: {
                feature: {
                    dataZoom: {
                        yAxisIndex: 'none'
                    },
                    restore: {},
                    saveAsImage: {}
                }
            },
            legend: {
                type: 'scroll',
                bottom: 0,
                data: series.map(s => s.name)
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '20%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: Array.from({ length: maxLength }, (_, i) => `Mois ${i}`),
                name: 'Mois depuis inscription'
            },
            yAxis: {
                type: 'value',
                name: 'Nombre de clients'
            },
            series: series
        };
    };

    const toggleCohortSelection = (cohort) => {
        setSelectedCohorts(prev => {
            if (prev.includes(cohort)) {
                return prev.filter(c => c !== cohort);
            } else {
                return [...prev, cohort];
            }
        });
    };

    const getChartOptionsForType = () => {
        switch (chartType) {
            case 'heatmap':
                return getHeatmapOptions();
            case 'retentionCurves':
                return getRetentionCurvesOptions();
            case 'cumulativeRetention':
                return getCumulativeRetentionOptions();
            case 'averageRetention':
                return getAverageRetentionCurveOptions();
            default:
                return getChartOptions();
        }
    };

    const buttonStyle = (isActive) => ({
        padding: '8px 16px',
        border: '1px solid #ddd',
        background: isActive ? '#1976d2' : '#fff',
        color: isActive ? '#fff' : '#333',
        cursor: 'pointer',
        borderRadius: '4px',
        fontSize: '14px'
    });

    return (
        <div className="enhanced-chart">
            <div style={{ marginBottom: 20, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                    onClick={() => setChartType('line')}
                    className={chartType === 'line' ? 'active' : ''}
                    style={buttonStyle(chartType === 'line')}
                >
                    Vue globale
                </button>
                <button
                    onClick={() => setChartType('bar')}
                    className={chartType === 'bar' ? 'active' : ''}
                    style={buttonStyle(chartType === 'bar')}
                >
                    Barres
                </button>
                <button
                    onClick={() => setChartType('retentionCurves')}
                    className={chartType === 'retentionCurves' ? 'active' : ''}
                    style={buttonStyle(chartType === 'retentionCurves')}
                >
                    Courbes de rétention
                </button>
                <button
                    onClick={() => setChartType('cumulativeRetention')}
                    className={chartType === 'cumulativeRetention' ? 'active' : ''}
                    style={buttonStyle(chartType === 'cumulativeRetention')}
                >
                    Rétention cumulée
                </button>
                <button
                    onClick={() => setChartType('averageRetention')}
                    className={chartType === 'averageRetention' ? 'active' : ''}
                    style={buttonStyle(chartType === 'averageRetention')}
                >
                    Moyenne
                </button>
                <button
                    onClick={() => setChartType('heatmap')}
                    className={chartType === 'heatmap' ? 'active' : ''}
                    style={buttonStyle(chartType === 'heatmap')}
                >
                    Heatmap
                </button>
                {!['heatmap', 'averageRetention'].includes(chartType) && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <input
                            type="checkbox"
                            checked={showArea}
                            onChange={(e) => setShowArea(e.target.checked)}
                        />
                        Afficher l'aire
                    </label>
                )}
            </div>

            {['retentionCurves', 'cumulativeRetention'].includes(chartType) && (
                <div style={{ marginBottom: 20, padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
                    <div style={{ marginBottom: 10, fontWeight: 'bold' }}>
                        Sélectionner les cohortes à afficher (max 12 dernières par défaut):
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                        {Object.keys(data).map(cohort => (
                            <button
                                key={cohort}
                                onClick={() => toggleCohortSelection(cohort)}
                                style={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    border: '1px solid #ddd',
                                    background: selectedCohorts.includes(cohort) ? '#4CAF50' : '#fff',
                                    color: selectedCohorts.includes(cohort) ? '#fff' : '#333',
                                    cursor: 'pointer',
                                    borderRadius: '3px'
                                }}
                            >
                                {cohort}
                            </button>
                        ))}
                        {selectedCohorts.length > 0 && (
                            <button
                                onClick={() => setSelectedCohorts([])}
                                style={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    border: '1px solid #ddd',
                                    background: '#f44336',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    borderRadius: '3px'
                                }}
                            >
                                Réinitialiser
                            </button>
                        )}
                    </div>
                </div>
            )}

            <ReactEcharts
                option={getChartOptionsForType()}
                style={{ height: '600px', width: '100%' }}
                notMerge={true}
                lazyUpdate={true}
            />
        </div>
    );
};

export default EnhancedChart;